import { User } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserDetails } from 'types';
import { Subscription } from 'types';

import { DISPOSABLE_EMAILS_DOMAINS } from '@/constants/disposable_emails';

import { createClient } from './supabase/component';
import { isTeamsPlan } from './team';

type UserContextType = {
  user: User | null;
  userDetails: UserDetails | null;
  isLoading: boolean;
  subscription: Subscription | null;
  isFreePlan: boolean;
  isTeamsPlan: boolean;
  updateUserDetails?: (details: Partial<UserDetails>) => void;
};

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export interface Props {
  [propName: string]: any;
}

const FREE_SUBSCRIPTION: Partial<Subscription> = {
  id: 'free',
  prices: {
    id: 'free_1',
    interval: 'month',
    currency: 'USD',
    products: {
      id: 'free_1',
      name: 'Free',
      active: true,
      description: null,
      image: null,
      is_one_time: false,
      metadata: {},
    },
  },
};

export const MyUserContextProvider = (props: Props) => {
  const supabase = useMemo(() => createClient(), []);
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingData, setIsloadingData] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const userId = user?.id;
  const userEmail = user?.email;
  // Tracks whether this browser was ever identified, so the sign-out reset
  // below only fires on a real sign-out.
  const wasIdentifiedRef = useRef(false);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !session)) {
        // An anonymous initial session has finished loading too.
        setUser(null);
        setIsLoadingUser(false);
        // Drop the identified person so the next visitor on this browser
        // starts a fresh anonymous identity instead of having their events
        // merged into the account that just left.
        //
        // Guarded: SIGNED_OUT can also arrive for a visitor who was never
        // signed in, and resetting then would mint a new anonymous id on
        // every such load and inflate unique-visitor counts.
        if (wasIdentifiedRef.current) {
          wasIdentifiedRef.current = false;
          posthog.reset();
        }
      } else if (session) {
        setUser(session.user);
        setIsLoadingUser(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase.auth]);

  // Identify as soon as a session exists, on EVERY authenticated page.
  //
  // This used to happen only on /dashboard, so anyone who signed up and never
  // opened the dashboard stayed split across two PostHog persons: the
  // anonymous one that browsed the marketing pages, and the identified one
  // that the server-side `Sign Up Success` webhook created. Measured before
  // this change, only 48% of signup persons had ANY pageviews attached, which
  // silently broke every acquisition funnel — you could not tell which landing
  // page produced a signup. Identifying here links the anonymous browsing
  // history to the account for the other half too.
  //
  // `posthog.identify` is idempotent, and /dashboard still calls it with the
  // richer subscription properties; this only guarantees the id is set early.
  useEffect(() => {
    if (!userId) return;
    wasIdentifiedRef.current = true;
    posthog.identify(userId, userEmail ? { email: userEmail } : undefined);

    // Fire `Sign Up Completed` on the account's FIRST authenticated session.
    //
    // The server-side `Sign Up Success` (Supabase DB webhook) is the reliable
    // count but structurally has no browser context; this client-side twin
    // carries the session/referrer/UTMs, so it is the conversion goal for
    // acquisition experiments. Two detection paths, either suffices:
    //  - first session: last_sign_in_at within minutes of created_at (covers
    //    OAuth and quickly-confirmed email signups; on any later login the
    //    gap is large, so it can't fire for returning users), or
    //  - the `ep_pending_signup` flag set by our signup forms (covers email
    //    confirmations opened hours later in the same browser).
    // A cross-device email confirmation matches neither and is deliberately
    // not counted here — the browsing context belongs to a different device.
    //
    // Guard is keyed by user id so a second person signing up in this browser
    // still gets counted. Cross-device double-fires within the first minutes
    // are possible and harmless: conversion queries count unique persons.
    try {
      const guardKey = 'ep_signup_completed';
      if (localStorage.getItem(guardKey) === userId) return;
      const createdAt = user?.created_at
        ? new Date(user.created_at).getTime()
        : NaN;
      const lastSignInAt = user?.last_sign_in_at
        ? new Date(user.last_sign_in_at).getTime()
        : createdAt;
      const isFirstSession =
        Number.isFinite(createdAt) &&
        Math.abs(lastSignInAt - createdAt) < 10 * 60 * 1000;
      const hadPendingSignup = !!localStorage.getItem('ep_pending_signup');
      if (isFirstSession || hadPendingSignup) {
        localStorage.setItem(guardKey, userId);
        localStorage.removeItem('ep_pending_signup');
        posthog.capture('Sign Up Completed', {
          method: user?.app_metadata?.provider || 'email',
        });
      }
    } catch {
      // Analytics must never break auth.
    }
  }, [userId, userEmail, user]);

  const getUserDetails = useCallback(
    () =>
      supabase
        .from('profiles')
        .select('*, profiles (first_name, last_name)')
        .eq('id', userId || '')
        .single(),
    [supabase, userId],
  );
  const getSubscription = useCallback(
    () =>
      supabase
        .from('subscriptions')
        .select('*, prices(*, products(*))')
        .in('status', ['trialing', 'active'])
        .single(),
    [supabase],
  );

  useEffect(() => {
    // console.log(
    //   user && !isLoadingData && !userDetails && !subscription,
    //   user,
    //   isLoadingData,
    //   userDetails,
    //   subscription
    // );
    let isCancelled = false;

    if (userId && !isLoadingUser) {
      setIsloadingData(true);
      Promise.allSettled([getUserDetails(), getSubscription()]).then(
        (results) => {
          if (isCancelled) return;

          const userDetailsPromise = results[0];
          const subscriptionPromise = results[1];

          if (userDetailsPromise.status === 'fulfilled') {
            // The API key is never kept in client state (Settings → API
            // asks /api/api-keys for it). Until docs/api-keys-migrate.sql
            // empties profiles.api_key, the row still carries it.
            const details = userDetailsPromise.value.data as UserDetails | null;
            if (details) delete details.api_key;
            setUserDetails(details);
            if (
              DISPOSABLE_EMAILS_DOMAINS.some(
                (domain) => userEmail && userEmail.includes(domain),
              )
            ) {
              //logout
              supabase.auth.signOut();
            }
          }

          if (subscriptionPromise.status === 'fulfilled') {
            if (subscriptionPromise.value.data)
              setSubscription(subscriptionPromise.value.data);
            else {
              setSubscription(FREE_SUBSCRIPTION as Subscription);
            }
          }

          // if (userDetailsPromise.status === 'fulfilled') {
          //   if (userDetailsPromise.value.data?.manual_subscription) {
          //     setSubscription(APPSUMO_SUBSCRIPTION as Subscription);
          //   }
          // }

          setIsloadingData(false);
        },
      );
    } else if (!userId && !isLoadingUser) {
      setIsloadingData(false);
      setUserDetails((current) => (current ? null : current));
      setSubscription((current) => (current ? null : current));
    }
    // else if (!subscription) {
    //   setSubscription(FREE_SUBSCRIPTION as Subscription);
    // }
    return () => {
      isCancelled = true;
    };
  }, [
    getSubscription,
    getUserDetails,
    isLoadingUser,
    supabase.auth,
    userEmail,
    userId,
  ]);

  const updateUserDetails = useCallback((details: Partial<UserDetails>) => {
    setUserDetails((current) =>
      current ? { ...current, ...details } : current,
    );
  }, []);

  const value = useMemo(
    () => ({
      user,
      userDetails,
      isLoading: isLoadingUser || isLoadingData,
      isFreePlan: subscription?.prices?.products?.name === 'Free',
      isTeamsPlan: isTeamsPlan(subscription as Subscription),
      subscription,
      updateUserDetails,
    }),
    [
      isLoadingData,
      isLoadingUser,
      subscription,
      updateUserDetails,
      user,
      userDetails,
    ],
  );

  return <UserContext.Provider value={value} {...props} />;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a MyUserContextProvider.`);
  }
  return context;
};
